import { useState, useEffect } from "react";
import CustomTabs from "@/shared/components/common/tab";
import StackKit from "@/shared/components/kit/stack";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchUserLastInquiry } from "@/api/user/home";
import ProviderStep from "./steps/ProviderStep";
import FinancialOtpStep from "./steps/FinancialOtpStep";
// import FinancialFinalStep from "./steps/FinancialFinalStep";
import JudicialOtpStep from "./steps/JudicialOtpStep";
import ValidationStatus from "./steps/validationStatus";
import Finish from "@/modules/user/components/credits/validation/payment/validationAfterPayment/steps/Finish";
import GuarantorStep from "@/modules/user/components/credits/validation/payment/validationAfterPayment/steps/guarantorStep";
import ContentValidation from "@/modules/user/components/credits/validation/payStep";

const TABS = {
    PROVIDER: 0,
    INTRO: 1,
    SELECT_GUARANTOR:2,
    FINANCIAL_OTP: 3,
    // FINANCIAL_FINAL: 2,
    JUDICIAL_OTP: 4,
    FINISH: 5,
} as const;

const STEP_TO_TAB: Record<string, number> = {
    'reject-loan': TABS.PROVIDER,
    set_provider_type: TABS.PROVIDER,
    set_pay: TABS.INTRO,
    set_guarantor_type: TABS.SELECT_GUARANTOR,
    select_guarantor_failed: TABS.SELECT_GUARANTOR,
    detect_financial_validator_failed: TABS.SELECT_GUARANTOR,
    verify_other_guarantor: TABS.FINANCIAL_OTP,
    verify_myself_guarantor: TABS.FINANCIAL_OTP,
    detect_financial_validator: TABS.FINANCIAL_OTP,
    send_otp_financial: TABS.FINANCIAL_OTP,
    send_otp_financial_failed: TABS.FINANCIAL_OTP,
    get_financial_confirm_otp: TABS.FINANCIAL_OTP,
    make_financial_report_failed: TABS.FINANCIAL_OTP,
    make_financial_report_success: TABS.FINANCIAL_OTP,
    make_financial_report_file_pending: TABS.FINANCIAL_OTP,
    make_financial_report_file: TABS.FINANCIAL_OTP,
    make_financial_report_file_failed: TABS.FINANCIAL_OTP,
    // download_financial_report_file: TABS.FINANCIAL_FINAL,
    // download_financial_report_success: TABS.FINANCIAL_FINAL,
    // download_financial_report_file_failed: TABS.FINANCIAL_FINAL,
    // resend_otp_financial: TABS.FINANCIAL_FINAL,
    // resend_otp_financial_success: TABS.FINANCIAL_FINAL,
    // resend_otp_financial_failed: TABS.FINANCIAL_FINAL,
    // Comment out ALL judicial steps
    finish: TABS.FINISH,

    download_financial_report_file: TABS.JUDICIAL_OTP,
    download_financial_report_success: TABS.JUDICIAL_OTP,
    download_financial_report_file_failed: TABS.JUDICIAL_OTP,
    resend_otp_financial: TABS.JUDICIAL_OTP,
    resend_otp_financial_success: TABS.JUDICIAL_OTP,
    resend_otp_financial_failed: TABS.JUDICIAL_OTP,

    'make_financial_report_file_success': TABS.JUDICIAL_OTP,
    'selector-judicial-reload': TABS.JUDICIAL_OTP,
    'download_financial_report_file_success': TABS.JUDICIAL_OTP,
    'selector-judicial': TABS.JUDICIAL_OTP,
    'selector-judicial-success': TABS.JUDICIAL_OTP,
    'selector-judicial-failed': TABS.JUDICIAL_OTP,
    get_judicial_confirm_otp: TABS.JUDICIAL_OTP,
    get_judicial_confirm_otp_success: TABS.JUDICIAL_OTP,
    get_judicial_confirm_otp_failed: TABS.JUDICIAL_OTP,
    judicial_resend_otp: TABS.JUDICIAL_OTP,
    judicial_resend_otp_success: TABS.JUDICIAL_OTP,
    judicial_resend_otp_failed: TABS.JUDICIAL_OTP,
    get_judicial_report: TABS.JUDICIAL_OTP,
    get_judicial_report_success: TABS.JUDICIAL_OTP,
    get_judicial_report_failed: TABS.JUDICIAL_OTP,
};

const isStepInTab = (step: string | undefined, tabIndex: number): boolean =>
    !!step && STEP_TO_TAB[step] === tabIndex;

const ValidationAfterPayment = ({ dataUserPAyment }: { dataUserPAyment: any }) => {
    const [activeTab, setActiveTab] = useState(() => {
        const step = dataUserPAyment?.step;
        if (isStepInTab(step, TABS.PROVIDER)) return TABS.PROVIDER;
        if (isStepInTab(step, TABS.INTRO)) return TABS.INTRO;
        if (isStepInTab(step, TABS.SELECT_GUARANTOR)) return TABS.SELECT_GUARANTOR;
        if (isStepInTab(step, TABS.FINANCIAL_OTP)) return TABS.FINANCIAL_OTP;
        if (isStepInTab(step, TABS.FINISH)) return TABS.FINISH;
        if (isStepInTab(step, TABS.JUDICIAL_OTP)) return TABS.JUDICIAL_OTP;
        return TABS.INTRO;
    });
    const goToNextStep = () => {
        setActiveTab(TABS.INTRO);
    };

    const [validationStatus, setValidationStatus] = useState<boolean>(false);
    // const [judicialValidationStatus, setJudicialValidationStatus] = useState<boolean>(false);

    const handleValidationComplete = () => {
        // console.log("✅ Validation complete!");
        // setValidationStatus(true);
        // window.location.reload();
        window.location.href = '/';
    };

    const { data, isLoading, isPending, refetch } = useQuery({
        queryKey: ["payment-myself-walet-balance"],
        queryFn: fetchUserLastInquiry,
        enabled: false,
    });

    const [hasFetchedUserData, setHasFetchedUserData] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    useEffect(() => {
        if (!hasFetchedUserData) {
            refetch().finally(() => setHasFetchedUserData(true));
        }
    }, [refetch, hasFetchedUserData]);

    useEffect(() => {
        if (!isPending && hasFetchedUserData) setIsLoadingData(false);
    }, [isPending, hasFetchedUserData]);

    const goToIntro = () => setActiveTab(TABS.INTRO);
    const goToGuarantor = () => setActiveTab(TABS.SELECT_GUARANTOR);
    const goToFinancialOtp = () => setActiveTab(TABS.FINANCIAL_OTP);
    // const goToFinancialFinal = () => setActiveTab(TABS.FINANCIAL_FINAL);
    // const goToJudicialOtp = () => setActiveTab(TABS.JUDICIAL_OTP);
    const goToFinish = () => setActiveTab(TABS.FINISH);

    if (isLoadingData || isPending || (!data && !hasFetchedUserData)) {
        return (
            <StackKit direction="row" sx={{ width: "100%", justifyContent: "center", height: "486px", marginBottom: "30px", padding: "10px 30px", alignItems: "center" }}>
                <CircularProgress />
            </StackKit>
        );
    }

    const tabData = [
        {
            label: "",
            content:<ProviderStep onSuccess={goToIntro} /> ,
            successText: "",
            disabled: activeTab !== TABS.PROVIDER,
        },
        {
            label: "مرحله اول: انتخاب تامین کننده مالی",
            content:<ContentValidation onSuccess={goToGuarantor} />  ,
            successText: "",
            disabled: activeTab !== TABS.INTRO,
        },
        {
            label: "مرحله دوم: معرفی صاحب اعتبار",
            content: <GuarantorStep data={data} onSuccess={goToFinancialOtp} />,
            successText: "",
            disabled: activeTab !== TABS.SELECT_GUARANTOR,
        },
        {
            label: "مرحله سوم: استعلام مالی",
            content: <FinancialOtpStep phone={data?.phone || ""} onSuccess={() => {}}/>,
            successText: "",
            disabled: activeTab !== TABS.FINANCIAL_OTP,
        },
        {
            label: "",
            content: <JudicialOtpStep onSuccess={() => {goToFinish()}} />,
            successText: "",
            disabled: activeTab !== TABS.JUDICIAL_OTP,
        },
        {
            label: "",
            content: <Finish onSuccess={() => {handleValidationComplete(); }} />,
            successText: "",
            disabled: activeTab !== TABS.FINISH,
        },
    ];

    return (
        <StackKit direction="row" sx={{ width: "100%", justifyContent: "space-between", height: "486px", marginBottom: "30px", padding: "10px 30px", alignItems: "center" }}>
            <StackKit sx={{ width: "100%", height: "100%" }}>
                <CustomTabs tabs={tabData} successTab={false} activeTab={activeTab} successSecondTab={false} contentFinalTab={null} contentValidation={<ValidationStatus />} validationStatus={validationStatus}
                />
            </StackKit>
        </StackKit>
    );
};

export default ValidationAfterPayment;